/**
 * @typedef {object} User
 * @property {string} id
 * @property {string} name Full name. Minimum of 2 characters in length
 * @property {string} email
 * @property {string} password
 * @property {string} [employeeId]
 * @property {Array<object>} userList The user list at user belongs to. A user can be in only one user list
 * @property {Array<object>} groups The groups a user is member of
 * @property {string} [company] Company name
 * @property {string} [phone] Phone number
 * @property {string} state The state of a user. Defaults to `ACTIVE`
 * @property {boolean} secondFactorEnabled  Defaults to `false`
 * @property {boolean} forceChangePassword Force a user to change the password after a successful login.
 * @property {boolean} [forceSecondFactor=false] Force a user to configure second factor. Defaults to `false`
 * @property {string} passwordResetHash A hash used when the user intends to recover her password
 * @property {object} [twoFactorData] Data used for storing the two factor authentication info
 * @property {Date} [lastConnectionTime] The last time a user was connected
 * @property {string} kind Kind of user: 'normal', 'admin', or 'superAdmin'
 * @property {boolean} noc User is a NOC user
 * @property {Date} passwordUpdatedAt The last time password was updated
 * @property {Array<string>} passwordHistory Tracks previous password hashes to disallow user from re-using a password
 * @property {object} integrations Stores data related to integrations such as Nokē Padlocks
 * @property {Array<object>} credentials All the credentials belonging to a user
 * @property {boolean} deleted Soft delete flag
 */


/* @ngInject */
export default function UsersService($q, $sails, $log) {
  const service = this;

  service.getAllUserLists = getAllUserLists;
  service.getAllUsers = getAllUsers;
  service.populateAllInfoForUserList = populateAllInfoForUserList;
  service.saveUser = saveUser;
  service.resetUsersPassword = resetUsersPassword;
  service.generateRandomPassword = generateRandomPassword;
  service.createUser = createUser;
  service.clearUsersTwoFactorSettings = clearUsersTwoFactorSettings;
  service.deleteUser = deleteUser;
  service.changeState = changeState;
  service.getGroupsForUser = getGroupsForUser;
  service.getCurrentUserGroups = getCurrentUserGroups;

  function getAllUserLists() {
    return $sails.get('/userlist')
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  /**
   * Retrieve all users from every list
   * ! Invoke with caution !
   *
   * @returns {Array<User>}
   */
  async function getAllUsers() {
    const { data: lists } = await getAllUserLists();
    // eslint-disable-next-line unicorn/no-await-expression-member -- TIF
    const allUsers = await Promise.all(lists.map(async ({ id }) => (await populateAllInfoForUserList(id)).users));
    return allUsers.reduce((users, usersInList) => {
      users.push(..._.values(usersInList));
      return users;
    }, []);
  }

  function populateAllInfoForUserList(userListId) {
    return $sails.get(`/userlist/${userListId}`)
      .then((success) => {
        let populatedList;
        if (success.data) {
          populatedList = success.data;
          populatedList.users =
              _.reduce(populatedList.users, (users, user) => _.assign(users, { [user.id]: user }), {});
        }
        return populatedList || {};
      })
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function saveUser(user) {
    return $sails.put(`/user/${user.id}`, user)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function resetUsersPassword(userEmail) {
    return $sails.post('/sendPassword', { email: userEmail })
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function generateRandomPassword(userOrg) {
    return $sails.get(`/generateRandomPassword/${userOrg}`)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function createUser(user) {
    return $sails.post('/user', user)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function clearUsersTwoFactorSettings(userId) {
    return $sails.post('/auth/secondFactorReset', { userId })
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function deleteUser(userId) {
    return $sails.delete(`/user/${userId}`)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function changeState(userId, state) {
    return $sails.put(`/user/${userId}/changestate`, { state })
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function getGroupsForUser(userId) {
    return $sails.get(`/user/${userId}/group`)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function getCurrentUserGroups() {
    return $sails.get('/group')
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

}
